import { setSvgIconSize } from 'LuxKit/SvgIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import clsx from 'clsx'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { rem } from 'polished'
import type { PropsWithChildren, ReactElement, ReactNode } from 'react'
import styled from 'styled-components'
import { tierGradientSecondaryBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'

const Icon = styled.div`
  flex-shrink: 0;
  flex-grow: 0;
  display: grid;
  place-items: center;
  ${setSvgIconSize('S')}
`

const EndContent = styled.div`
  flex-shrink: 0;
  display: flex;

  &:empty { display: none; }
`

const Content = styled(BodyText)`
  flex-grow: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(12)};
  min-width: 0;
`

/**
 * Keeps the message and anything inline with it (a `Details` link, say) together as one flex item, so
 * `Content`'s `space-between` separates the message from `EndContent` rather than splitting the
 * message's own parts apart.
 */
const Message = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: ${rem(4)};
  min-width: 0;
`

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${rem(8)};
  width: 100%;
  min-height: ${rem(48)};
  padding-block: ${rem(8)};
  padding-inline: ${rem(16)} ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.L};

  /* Without an interactive trailing control there's no button inset to absorb, so pad the end fully. */
  &:not(:has(${EndContent} button), :has(${EndContent} a)) {
    padding-inline-end: ${rem(16)};
  }

  ${tierGradientSecondaryBackgroundStyle}
  ${tierNormalContrastColorStyle}
`

interface Props extends PropsWithChildren {
  tier: App.LoyaltyTier
  /** Leading icon. Absent by default — this banner is usually text-first. */
  icon?: ReactElement | false
  /** Trailing control, e.g. an apply/remove chip. */
  endContent?: ReactNode
  className?: string
}

/**
 * A compact tier-coloured bar offering a stay upgrade on a rate, with a trailing control.
 */
function LoyaltyTierStayUpgradeMessageBanner({
  tier,
  icon,
  endContent,
  className,
  children,
}: Props) {
  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <Container
      className={clsx(`tier-${tier}`, className)}
    >
      {!!icon && <Icon>{icon}</Icon>}
      <Content variant="medium" weight="bold">
        <Message>{children}</Message>
        {!!endContent && <EndContent>{endContent}</EndContent>}
      </Content>
    </Container>
  </ProductPaletteProvider>
}

export default LoyaltyTierStayUpgradeMessageBanner
